import React from 'react';
import { motion } from 'framer-motion';
import { SlideLayout } from '../SlideLayout';
import { Binary, Sparkles, Database } from 'lucide-react';
export function CustomHardwareSlide() {
  const features = [{
    icon: Binary,
    title: 'Arbitrary Bit-Width Arithmetic',
    desc: 'FPGAs implement 1-bit, 2-bit ternary, INT4, or any custom precision in native logic — no overhead from emulating low precision on FP32/FP16 datapaths.',
    example: 'TerEffic demonstrated this for ternary LLMs, achieving massive efficiency gains impossible on fixed GPU architectures.',
    source: '[28]'
  }, {
    icon: Sparkles,
    title: 'Native Sparse Computation',
    desc: 'FPGAs natively skip zero-valued weights in sparse models with no wasted cycles. GPU sparse compute requires special tensor core modes with limited flexibility.',
    example: "FlightLLM's sparse DSP chains process only non-zero weights, eliminating the wasted compute that plagues GPU sparse modes.",
    source: '[27]'
  }, {
    icon: Database,
    title: 'Custom KV Cache Management',
    desc: 'Liveness-driven two-tier caches can be physically implemented to match the exact reuse pattern of attention, reducing irregular HBM access.',
    example: "Hardware-level cache policies tuned to transformer attention patterns — something impossible with GPU's fixed cache hierarchy.",
    source: '[29]'
  }];
  return <SlideLayout slideNumber={14} totalSlides={20} title="Hardware Customization" data-id="element-86">
      <div className="w-full h-full flex flex-col" data-id="element-87">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-5" data-id="element-88">
          <h2 className="text-4xl md:text-5xl font-bold mb-2" data-id="element-89">
            The Hardware Adapts to the{' '}
            <span className="text-brand-primary" data-id="element-90">Algorithm</span>
          </h2>
          <p className="text-2xl text-brand-muted max-w-3xl mx-auto" data-id="element-91">
            Not the other way around. FPGAs implement algorithm-specific
            optimizations that fixed architectures simply cannot.
          </p>
        </motion.div>

        <div className="space-y-4 flex-1 flex flex-col justify-center" data-id="element-92">
          {features.map((feature, index) => <motion.div key={feature.title} initial={{
          opacity: 0,
          x: index % 2 === 0 ? -20 : 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: index * 0.15 + 0.2
        }} className="bg-brand-surface border border-brand-primary/20 rounded-2xl p-6 flex flex-col md:flex-row gap-5" data-id="element-93">
              <div className="shrink-0" data-id="element-94">
                <div className="p-4 bg-gradient-to-br from-brand-primary/20 to-brand-primary/5 rounded-xl text-brand-primary" data-id="element-95">
                  <feature.icon size={40} data-id="element-96" />
                </div>
              </div>
              <div className="flex-1" data-id="element-97">
                <div className="flex items-center gap-2 mb-2" data-id="element-98">
                  <h3 className="text-2xl font-bold text-white" data-id="element-99">
                    {feature.title}
                  </h3>
                  <span className="text-base font-mono text-brand-muted bg-brand-dark px-2 py-0.5 rounded" data-id="element-100">
                    {feature.source}
                  </span>
                </div>
                <p className="text-brand-muted text-xl leading-relaxed mb-3" data-id="element-101">
                  {feature.desc}
                </p>
                <div className="bg-brand-dark rounded-lg p-3 border-l-2 border-brand-primary/40" data-id="element-102">
                  <p className="text-lg text-brand-muted italic" data-id="element-103">
                    {feature.example}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </SlideLayout>;
}